/**
 * Workflow Validator
 * Validates workflow definitions for correctness and completeness
 */

import {
  WorkflowDefinition,
  WorkflowNode,
  WorkflowEdge,
  ValidationResult,
  ValidationError
} from './types';

export class WorkflowValidator {
  /**
   * Validate a complete workflow definition
   */
  static validate(workflow: WorkflowDefinition): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    // Basic validation
    if (!workflow.name || workflow.name.trim() === '') {
      errors.push({
        field: 'name',
        message: 'Workflow name is required',
        severity: 'error'
      });
    }

    if (!workflow.nodes || !Array.isArray(workflow.nodes)) {
      errors.push({
        field: 'nodes',
        message: 'Workflow must have a nodes array',
        severity: 'error'
      });
      return { valid: false, errors, warnings };
    }

    if (!workflow.edges || !Array.isArray(workflow.edges)) {
      errors.push({
        field: 'edges',
        message: 'Workflow must have an edges array',
        severity: 'error'
      });
      return { valid: false, errors, warnings };
    }

    // Validate nodes
    const nodeValidation = this.validateNodes(workflow.nodes);
    errors.push(...nodeValidation.errors);
    warnings.push(...nodeValidation.warnings);

    // Validate edges
    const edgeValidation = this.validateEdges(workflow.edges, workflow.nodes);
    errors.push(...edgeValidation.errors);
    warnings.push(...edgeValidation.warnings);

    // Check for disconnected nodes
    const disconnectedNodes = this.findDisconnectedNodes(workflow.nodes, workflow.edges);
    if (disconnectedNodes.length > 0) {
      warnings.push({
        field: 'nodes',
        message: `Disconnected nodes found: ${disconnectedNodes.join(', ')}`,
        severity: 'warning'
      });
    }

    // Check for cycles
    if (this.hasCycles(workflow.nodes, workflow.edges)) {
      warnings.push({
        field: 'edges',
        message: 'Workflow contains cycles - may cause infinite loops',
        severity: 'warning'
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate nodes array
   */
  private static validateNodes(nodes: WorkflowNode[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const nodeIds = new Set<string>();

    nodes.forEach((node, index) => {
      // Check for required fields
      if (!node.id) {
        errors.push({
          field: `nodes[${index}].id`,
          message: 'Node ID is required',
          severity: 'error'
        });
      } else {
        // Check for duplicate IDs
        if (nodeIds.has(node.id)) {
          errors.push({
            field: `nodes[${index}].id`,
            message: `Duplicate node ID: ${node.id}`,
            severity: 'error'
          });
        }
        nodeIds.add(node.id);
      }

      if (!node.type) {
        errors.push({
          field: `nodes[${index}].type`,
          message: 'Node type is required',
          severity: 'error'
        });
      }

      if (!node.data || !node.data.label) {
        warnings.push({
          field: `nodes[${index}].data.label`,
          message: 'Node label is recommended',
          severity: 'warning'
        });
      }

      if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
        errors.push({
          field: `nodes[${index}].position`,
          message: 'Node must have valid position coordinates',
          severity: 'error'
        });
      }
    });

    return { valid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate edges array
   */
  private static validateEdges(edges: WorkflowEdge[], nodes: WorkflowNode[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const nodeIds = new Set(nodes.map(n => n.id));
    const edgeIds = new Set<string>();

    edges.forEach((edge, index) => {
      // Check for required fields
      if (!edge.id) {
        errors.push({
          field: `edges[${index}].id`,
          message: 'Edge ID is required',
          severity: 'error'
        });
      } else {
        // Check for duplicate IDs
        if (edgeIds.has(edge.id)) {
          errors.push({
            field: `edges[${index}].id`,
            message: `Duplicate edge ID: ${edge.id}`,
            severity: 'error'
          });
        }
        edgeIds.add(edge.id);
      }

      if (!edge.source) {
        errors.push({
          field: `edges[${index}].source`,
          message: 'Edge source is required',
          severity: 'error'
        });
      } else if (!nodeIds.has(edge.source)) {
        errors.push({
          field: `edges[${index}].source`,
          message: `Edge source node not found: ${edge.source}`,
          severity: 'error'
        });
      }

      if (!edge.target) {
        errors.push({
          field: `edges[${index}].target`,
          message: 'Edge target is required',
          severity: 'error'
        });
      } else if (!nodeIds.has(edge.target)) {
        errors.push({
          field: `edges[${index}].target`,
          message: `Edge target node not found: ${edge.target}`,
          severity: 'error'
        });
      }
    });

    return { valid: errors.length === 0, errors, warnings };
  }

  /**
   * Find disconnected nodes (nodes with no incoming or outgoing edges)
   */
  private static findDisconnectedNodes(nodes: WorkflowNode[], edges: WorkflowEdge[]): string[] {
    if (nodes.length === 0) return [];
    if (nodes.length === 1) return []; // Single node is valid

    const connectedNodes = new Set<string>();
    edges.forEach(edge => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    return nodes
      .filter(node => !connectedNodes.has(node.id))
      .map(node => node.id);
  }

  /**
   * Check if workflow has cycles using DFS
   */
  private static hasCycles(nodes: WorkflowNode[], edges: WorkflowEdge[]): boolean {
    const adjacencyList = new Map<string, string[]>();

    // Build adjacency list
    nodes.forEach(node => adjacencyList.set(node.id, []));
    edges.forEach(edge => {
      const neighbors = adjacencyList.get(edge.source) || [];
      neighbors.push(edge.target);
      adjacencyList.set(edge.source, neighbors);
    });

    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const hasCycleDFS = (nodeId: string): boolean => {
      visited.add(nodeId);
      recursionStack.add(nodeId);

      const neighbors = adjacencyList.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (hasCycleDFS(neighbor)) return true;
        } else if (recursionStack.has(neighbor)) {
          return true;
        }
      }

      recursionStack.delete(nodeId);
      return false;
    };

    for (const node of nodes) {
      if (!visited.has(node.id)) {
        if (hasCycleDFS(node.id)) return true;
      }
    }

    return false;
  }
}

